import { BiSync, BiHelpCircle, BiDetail, BiCloudUpload } from "react-icons/bi";
import {
	MdEditNote,
	MdHelpOutline,
	MdOutlineChat,
	MdOutlineMap,
} from "react-icons/md";
import { ModulesCube, MyBolt } from "./customIcons";
import { useLocation } from "react-router-dom";
import { Actions } from "@typedefs/interfaces";
import type { IconType } from "react-icons";
import type { ReactElement } from "react";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector, usePermissions } from "@app/hooks";
import {
	selectCurrentBuilder,
	setActionList,
	setActiveAction,
} from "@features/builder/builderSlice";
import { TranslationKey } from "@typedefs/modulesInterfaces";
import { selectCurrentProject } from "@features/project/projectSlice";

import ModulesAction from "@components/actions/modules/ModulesAction";
import ChangesAction from "@components/actions/changes/ChangesAction";
import LiveUpdatesAction from "@components/actions/liveUpdate/LiveUpdatesAction";
import NotesAction from "@components/actions/notes/NotesAction";
import CommentsAction from "@components/actions/comments/CommentsAction";
import MapAction from "@components/actions/map/MapAction";
import DocumentationAction from "@components/actions/DocumentationAction";
import DefinitionsAction from "@components/actions/definitions/DefinitionsAction";
import AttachmentsAction from "@components/actions/attachments/AttachmentsAction";
import GuidanceAction from "@components/actions/guidance/GuidanceAction";

export type ActionsInfoType = {
	[key in Actions]: {
		label: TranslationKey;
		icon: IconType;
		color: string;
	};
};

export type ActionsComponentType = {
	[key in Actions]: ReactElement | null;
};

export const ActionsInfo: ActionsInfoType = {
	[Actions.LiveUpdates]: {
		label: "main.live_updates",
		icon: BiSync,
		color: "#750c0c",
	},
	[Actions.Notes]: {
		label: "main.notes",
		icon: MdEditNote,
		color: "#F9BD24",
	},
	[Actions.Comments]: {
		label: "main.comments",
		icon: MdOutlineChat,
		color: "var(--tertiary-deep)",
	},
	[Actions.Help]: {
		label: "main.tutorials",
		icon: MdHelpOutline,
		color: "#4E93C6",
	},
	[Actions.Changes]: {
		label: "main.activity",
		icon: MyBolt,
		color: "var(--quaternary-deep)",
	},
	[Actions.ProgressiveModules]: {
		label: "main.modules",
		icon: ModulesCube,
		color: "var(--common-dark)",
	},
	[Actions.Map]: {
		label: "main.map",
		icon: MdOutlineMap,
		color: "#750c0c",
	},
	[Actions.Documentation]: {
		label: "main.documentation",
		icon: BiHelpCircle,
		color: "#4E93C6",
	},
	[Actions.Definitions]: {
		label: "main.definitions",
		icon: BiDetail,
		color: "#50A2A7",
	},
	[Actions.Attachments]: {
		label: "main.uploads",
		icon: BiCloudUpload,
		color: "#F9BD24",
	},
};

export const ActionsComponent: ActionsComponentType = {
	[Actions.LiveUpdates]: <LiveUpdatesAction />,
	[Actions.Notes]: <NotesAction />,
	[Actions.Comments]: <CommentsAction />,
	[Actions.Help]: <GuidanceAction />,
	[Actions.Changes]: <ChangesAction />,
	[Actions.ProgressiveModules]: <ModulesAction />,
	[Actions.Map]: <MapAction />,
	[Actions.Documentation]: <DocumentationAction />,
	[Actions.Definitions]: <DefinitionsAction />,
	[Actions.Attachments]: <AttachmentsAction />,
};

const areEqual = (list1: Actions[], list2: Actions[]) => {
	if (list1.length !== list2.length) return false;
	for (let i = 0; i < list1.length; i++) {
		if (list1[i] !== list2[i]) return false;
	}
	return true;
};

const useTabsHook = () => {
	const dispatch = useAppDispatch();
	const { actionsList, activeAction } = useAppSelector(selectCurrentBuilder);
	const { pathname } = useLocation();
	const { project } = useAppSelector(selectCurrentProject);
	const { isPublic } = usePermissions();

	useEffect(() => {
		const pathList = pathname.split("/");
		const root = isPublic ? pathList[2] : pathList[1];
		const leaf = isPublic ? pathList[4] : pathList[3];

		const isProjectCreated = project?.id && project?.id !== 0;

		// Function to filter out restricted actions when in public mode or B-intact tool
		const filterActions = (actions: Actions[]): Actions[] => {
			let filteredActions = actions;

			// Filter out restricted actions when in public mode
			if (isPublic) {
				const restrictedActions = [Actions.Attachments, Actions.Notes, Actions.Comments];
				filteredActions = filteredActions.filter(action => !restrictedActions.includes(action));
			}

			return filteredActions;
		};

		const projectActionsMap: { [leaf: string]: Actions[] } = {
			"activities": filterActions([Actions.Attachments, Actions.Notes, Actions.Documentation]),
			"description": isProjectCreated
				? filterActions([Actions.Map, Actions.Documentation, Actions.Notes, Actions.Help])
				: [Actions.Map, Actions.Documentation, Actions.Help],
			"results": filterActions([Actions.Attachments, Actions.Notes]),
			"new-activity": [
				Actions.ProgressiveModules,
				Actions.Documentation,
				Actions.Definitions,
				Actions.Help
			]
		};

		const builderActionsMap: (key: string) => Actions[] = (leaf) => {
			if (leaf === "complete") return [Actions.LiveUpdates, Actions.Help];
			return filterActions([
				Actions.LiveUpdates,
				Actions.Comments,
				Actions.Notes,
				Actions.Help,
				Actions.Changes,
				Actions.Definitions,
			]);
		}

		const actions = root === "project" ? projectActionsMap[leaf] ?? [] : root === "builder" ? builderActionsMap(leaf) : [];
		if (!areEqual(actionsList, actions) && actions.length > 0) {
			dispatch(setActionList(actions));
			
			// Set Map as default for new projects on description page
			const isNewProject = !project?.id || project?.id === 0;
			const isDescriptionPage = leaf === "description";
			
			if (isNewProject && isDescriptionPage && actions.includes(Actions.Map)) {
				dispatch(setActiveAction(Actions.Map));
			} else if (!actions.includes(activeAction)) {
				dispatch(setActiveAction(actions[0]));
			}
		}
	}, [pathname, dispatch, project, isPublic, actionsList, activeAction]);

	return { isHeaderSmall: pathname === "/" || pathname.startsWith("/auth") };
};

export default useTabsHook;
